import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCatalog } from "../api.js";
import { buildIndex, flattenCatalog, searchCatalog } from "../lib/catalogSearch.js";

const MIN_QUERY = 2; // characters before the dropdown shows
const MAX_RESULTS = 7; // rows in the live dropdown
const CLOSE_DELAY = 2000; // ms to linger after the field loses focus before collapsing

// Module-level cache: the catalog is fetched once on first focus and reused by
// every CatalogSearch instance (navbar mounts a single one, but this also
// survives remounts during a session).
let indexPromise = null;
function loadIndex() {
  if (!indexPromise) {
    indexPromise = fetchCatalog()
      .then((groups) => buildIndex(flattenCatalog(groups)))
      .catch((e) => {
        indexPromise = null; // allow a retry on the next focus
        throw e;
      });
  }
  return indexPromise;
}

function thumb(item) {
  return item.imageUrl ?? item.image ?? null;
}

export default function CatalogSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const closeTimer = useRef(null);

  const [index, setIndex] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [expanded, setExpanded] = useState(false); // box revealed (icon → input)
  const [focused, setFocused] = useState(false); // input has focus (drives dropdown)
  const [active, setActive] = useState(-1); // highlighted row for keyboard nav

  const ensureIndex = useCallback(() => {
    if (index) return;
    loadIndex().then(setIndex).catch(() => {});
  }, [index]);

  // Recompute results whenever the query or the loaded index changes.
  useEffect(() => {
    const q = query.trim();
    if (!index || q.length < MIN_QUERY) {
      setResults([]);
      return;
    }
    setResults(searchCatalog(index, q, MAX_RESULTS));
  }, [query, index]);

  // Reset the keyboard highlight whenever the result set changes.
  useEffect(() => {
    setActive(-1);
  }, [results]);

  // Clear any pending collapse on unmount.
  useEffect(() => clearCloseTimer, []);

  // The dropdown is tied to focus, not the lingering `expanded` state, so it
  // vanishes the instant the field loses focus while the box still animates shut.
  const showDropdown = focused && query.trim().length >= MIN_QUERY;

  function clearCloseTimer() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }

  // The field lingers for a moment after losing focus before collapsing, so a
  // brief blur (or a click headed for a result) doesn't snap it shut.
  function scheduleClose() {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setExpanded(false);
    }, CLOSE_DELAY);
  }

  function open() {
    clearCloseTimer();
    setExpanded(true);
    ensureIndex();
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function close() {
    clearCloseTimer();
    setExpanded(false);
    inputRef.current?.blur();
  }

  function goToItem(item) {
    clearCloseTimer();
    setExpanded(false);
    setQuery("");
    navigate(`/catalog/${encodeURIComponent(item.sku)}`);
  }

  function goToResultsPage() {
    const q = query.trim();
    if (!q) return;
    clearCloseTimer();
    setExpanded(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  function onSubmit(e) {
    e.preventDefault();
    if (active >= 0 && results[active]) goToItem(results[active]);
    else goToResultsPage();
  }

  function onKeyDown(e) {
    if (e.key === "Escape") {
      close();
      return;
    }
    if (!showDropdown) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    }
  }

  return (
    <div
      ref={rootRef}
      className={`pmp-search${expanded ? " is-open" : ""}`}
      role="search"
    >
      <button
        type="button"
        className="pmp-search-toggle"
        aria-label={expanded ? "Close search" : "Search catalog"}
        aria-expanded={expanded}
        onClick={() => (expanded ? close() : open())}
      >
        <i className={`bi ${expanded ? "bi-x-lg" : "bi-search"}`} aria-hidden="true" />
      </button>

      <div className="pmp-search-pop">
        <form className="pmp-search-box" onSubmit={onSubmit} role="search">
          <i className="bi bi-search pmp-search-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            className="pmp-search-input"
            placeholder="Search products…"
            aria-label="Search products"
            aria-expanded={showDropdown}
            aria-controls={listId}
            aria-autocomplete="list"
            autoComplete="off"
            value={query}
            onFocus={() => {
              clearCloseTimer();
              ensureIndex();
              setExpanded(true);
              setFocused(true);
            }}
            onBlur={() => {
              setFocused(false); // close the dropdown immediately on blur…
              scheduleClose(); // …but let the box linger before it collapses
            }}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </form>

        {showDropdown ? (
          <div className="pmp-search-menu" id={listId} role="listbox">
            {results.length ? (
              <>
                {results.map((item, i) => {
                  const img = thumb(item);
                  return (
                    <button
                      type="button"
                      key={item.sku}
                      role="option"
                      aria-selected={i === active}
                      className={`pmp-search-result${i === active ? " is-active" : ""}`}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={(e) => e.preventDefault()} // keep input focus so the click lands before blur hides us
                      onClick={() => goToItem(item)}
                    >
                      <span className="pmp-search-result-thumb">
                        {img ? (
                          <img src={img} alt="" loading="lazy" />
                        ) : (
                          <i className="bi bi-box-seam" aria-hidden="true" />
                        )}
                      </span>
                      <span className="pmp-search-result-text">
                        <span className="pmp-search-result-name">{item.name}</span>
                        <span className="pmp-search-result-cat">
                          {[item.category, item.subCategory].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="pmp-search-seeall"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={goToResultsPage}
                >
                  See all results for “{query.trim()}” →
                </button>
              </>
            ) : (
              <div className="pmp-search-empty">
                <p className="mb-1 fw-semibold">No products match “{query.trim()}”.</p>
                <p className="mb-0 small text-secondary">
                  Try a different term, or request a quote — we source beyond the
                  catalog.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
